// Loads everything a content preview needs for one brief and normalizes it into a PreviewModel:
// the brief (from its campaign), its latest draft, selected visual slots (blog posts), the brand
// and the brand visual kit (with signed font URLs).
import { useMemo } from "react";
import { buildPreviewModel, contentTypeFor, previewSurface, type PreviewModel } from "../../../shared/content-preview";
import { isNotFoundError } from "../lib/load-state";
import { useBrand } from "./useBrand";
import { useBriefDocument, useCampaignDetail } from "./useCampaigns";
import { useBriefVisuals, useVisualKit } from "./useVisuals";

export interface ContentPreviewState {
  model: PreviewModel | null;
  isLoading: boolean;
  /** The brief is not in this campaign / brand. */
  notFound: boolean;
  error: unknown;
}

export function useContentPreview(brandId: string | undefined, campaignId: string | undefined, briefId: string | undefined): ContentPreviewState {
  const campaignQ = useCampaignDetail(brandId, campaignId);
  const brief = campaignQ.data?.data?.briefs?.find((candidate) => candidate.id === briefId && candidate.brand_id === brandId) ?? null;
  const draftQ = useBriefDocument(campaignId, briefId, "draft", !!brief);
  const isBlog = !!brief && contentTypeFor(previewSurface(brief)) === "blog";
  // Visual slots exist for blog posts only (PRD-0191); other surfaces use the brief's thumbnail.
  const visualsQ = useBriefVisuals(isBlog ? brandId : undefined, isBlog ? briefId : undefined);
  const brandQ = useBrand(brandId);
  const kitQ = useVisualKit(brandId);

  const draftMissing = draftQ.isError && isNotFoundError(draftQ.error);
  const content = draftQ.data?.data?.content ?? null;
  const brand = brandQ.data?.data ?? null;
  const kit = kitQ.data ?? null;
  const visuals = visualsQ.data ?? null;

  const model = useMemo(
    () => (brief ? buildPreviewModel({ brief, content: draftMissing ? null : content, visuals, brand, kit }) : null),
    [brief, content, draftMissing, visuals, brand, kit],
  );

  const isLoading = campaignQ.isLoading || (!!brief && (draftQ.isLoading || brandQ.isLoading || kitQ.isLoading || (isBlog && visualsQ.isLoading)));
  return {
    model,
    isLoading,
    notFound: !campaignQ.isLoading && !!campaignQ.data && !brief,
    error: campaignQ.error ?? (draftQ.isError && !draftMissing ? draftQ.error : null),
  };
}
